/**
 * Spec №11 (11.3.3) — the generic four-plane record page (`/app/place/:entity/:id`).
 * Any Tier E entity with a record view gets this for free (D-W1). One composite
 * read (`get_row_detail`) feeds all four planes:
 *   IS      — the declared record view (fields + metrics), forward refs resolved.
 *   DOES    — the work rail: tasks regarding this row + pending authorisations
 *             touching it (workflow slot reserved for №16).
 *   KNOW    — memories that reference this row.
 *   HISTORY — the activity timeline.
 * The record-scoped conversation is rendered globally by the shell (the work rail's
 * Chat tab) and scopes to this record through the route→anchor matrix. Locked visual
 * target: mockups/four-plane-app-shell.html.
 */
import { createElement } from "react";
import { Navigate, useLocation, useParams } from "react-router";
import { resolveSubject } from "./route-anchor-matrix";
import { hasSubView } from "./record-subviews";
import type { EntityRecord } from "../../../lib/types";
import { useEntityType } from "../../../lib/hooks/use-kuvio";
import { useRowDetail } from "../../hooks/use-row-detail";
import { usePlaceViews } from "../../hooks/use-place-views";
import { usePendingAuthorisations } from "../../hooks/use-ledger-approvals";
import { formatRelativeTime } from "../../lib/v3-columns";
import { RecordView } from "./RecordView";
import { ContextActionBar } from "./ContextActionBar";
import { recordRendererFor } from "./record-renderers";
import { useStageHead } from "./stage-head";
import { useContextActions, atPlacement } from "../../hooks/use-context-actions";

/**
 * №29 (S2) — the ONE record route. A type with a specialised surface (matter,
 * invoice, fee arrangement) renders it; everything else renders the generic
 * four-plane record below.
 *
 * The dispatch is here, at the route element, rather than inside `PlaceRecordPage`:
 * a specialised renderer must not first pay for the generic page's reads, and
 * returning early from inside `PlaceRecordPage` after its hooks have run would be a
 * rules-of-hooks violation waiting to happen. See `record-renderers.ts` for why this
 * is a registry rather than three routes.
 */
export function PlaceRecordRoute() {
  const { entity, id } = useParams<{ entity: string; id: string }>();
  const { pathname } = useLocation();

  // №29 (S4, §3 ④) — a sub-view URL this entity type does not declare lands on the
  // record instead of rendering. The sub-view PATTERNS are generic (`:entity`), because
  // which component draws a record is a registry read and which section it opens is a
  // route read — neither is worth six routes per type. The cost of that generality is
  // exactly this: `/app/place/contact/c1/billing` matches a real route, and without a
  // guard it would draw a matter's billing frame around a contact. Redirecting is the
  // honest answer — the record exists, that view of it does not.
  const row = resolveSubject(pathname);
  const declared = hasSubView(entity, row.key);
  const Specialised = recordRendererFor(entity);
  if (!declared && entity && id) {
    return <Navigate to={`/app/place/${entity}/${id}`} replace />;
  }
  return Specialised ? createElement(Specialised) : <PlaceRecordPage />;
}

export function PlaceRecordPage() {
  const { entity, id } = useParams<{ entity: string; id: string }>();
  const type = useEntityType(entity);
  const detail = useRowDetail(entity, id);
  const views = usePlaceViews(entity);
  const auths = usePendingAuthorisations();
  // №46 (R7 ③) — same registry read the Work panel's bar itself makes (react-query
  // dedupes it): whether the panel has an action to show is known before the panel
  // decides whether to mount at all, matching the record-action-bar card's own gate.
  const recordActions = useContextActions(entity, id);
  const hasActions = atPlacement(recordActions.data?.actions, "record_actions").length > 0;

  if (type.isLoading || detail.isLoading) {
    return (
      <div className="screen">
        <div className="card">
          <div className="skeleton sk-line" style={{ width: "40%" }} />
          <div className="skeleton sk-line" style={{ width: "80%" }} />
        </div>
      </div>
    );
  }
  if (!type.data) {
    return (
      <div className="screen">
        <div className="empty-state">
          <h3>Unknown place</h3>
          <p>No entity type "{entity}" is installed in this workspace.</p>
        </div>
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="screen">
        <div className="error-state">
          <h3>Couldn't load record</h3>
          <p>{String((detail.error as Error)?.message ?? "Unknown error")}</p>
        </div>
      </div>
    );
  }

  const d = detail.data;
  const e = d.entity as EntityRecord & Record<string, unknown>;
  const titleField =
    type.data.fields.find((f) => f.display_role === "title") ??
    type.data.fields.find((f) => f.display_field);
  const title = (titleField ? e[titleField.name] : undefined) as string | undefined;
  const wf = type.data.workflow;
  const statusVal = wf ? (e[wf.field ?? "status"] as string | undefined) : undefined;

  const tasks = d.back_refs?.tasks ?? [];
  const memories = d.back_refs?.memories ?? [];
  const hasActivity = "activities" in d;
  const activities = (hasActivity ? d.activities?.items : undefined) ?? [];
  const relatedAuths = (auths.data?.authorisations ?? []).filter((a) => a.subject_id === id);

  // №46 (R7 ③) — "a section with nothing to say is ABSENT, not empty." This is the
  // record's default landing view, not a place someone navigated to look for this
  // content, so each companion panel mounts only once it has something in it.
  // `hasRecordActivity` is deliberately distinct from `hasActivity`, which only
  // tests that the API returned the key at all. `hasWork` includes `hasActions`
  // because the record-action bar IS the panel having something to say, even
  // with no tasks/authorisations yet (constraint on stripping an affordance).
  const hasRecordActivity = hasActivity && activities.length > 0;
  const hasWork = hasActions || tasks.length > 0 || relatedAuths.length > 0;
  const hasMemories = memories.length > 0;

  return (
    <div className="screen">
      {/* №29 (S2) — the RECORD subject. The stage head already renders the record's
          id as the anchor, so the identity bar that used to repeat it here is gone:
          the TITLE becomes the head's working title and the lifecycle status becomes
          its state chip. What the head cannot carry stays on the stage. */}
      <RecordStageHead title={title ?? e.id} status={statusVal} />

      <div className="recordgrid">
        {/* left column: IS + HISTORY */}
        <div>
          <RecordView
            entityType={type.data}
            entity={e}
            forwardRefs={d.forward_refs}
            viewConfig={views.recordView}
          />

          {hasRecordActivity && (
            <div className="card" style={{ marginTop: 14 }}>
              <h3 style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 8, fontWeight: 500 }}>
                Activity
              </h3>
              <ul className="timeline">
                {activities.slice(0, 8).map((a) => (
                  <li key={a.id}>
                    <span className="t">{formatRelativeTime(a.occurred_at ?? a.created_at)}</span>
                    <div>
                      {a.summary ?? a.subject}
                      {a.actor_label && <span className="who"> · {a.actor_label}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* right column: DOES + KNOW */}
        <div>
          {/* №23 Stage C p1 — `region="sidebar"` sections (today: only `contact`'s
              Contact / Identifiers / Engagement) render here instead of inside
              the left column's card. Before this, RecordView never read `region`
              at all, so these three rendered — unlabelled as such — mixed in
              with Identity/Notes on the left. Renders nothing when the type
              declares no sidebar sections (unchanged for every other type). */}
          <RecordView
            entityType={type.data}
            entity={e}
            forwardRefs={d.forward_refs}
            viewConfig={views.recordView}
            region="sidebar"
          />

          {hasWork && (
            <div className="panel">
              <h3>Work</h3>
              {/* №13.1 (§7 / M30–M32) — cross-entity actions on the GENERIC record
                  page. The №11.1 registry resolves prefill + locked args; no
                  per-page wiring, no `only=` allow-list (show all resolved). It
                  is itself a real action — the panel has something to say even
                  when there are no tasks or authorisations yet. */}
              <ContextActionBar
                context={type.data.slug}
                id={id!}
                onDone={() => void detail.refetch()}
              />
              {tasks.slice(0, 6).map((t) => (
                <div className="workrow" key={t.id}>
                  <div className="meta">
                    <div className="nm">{t.title}</div>
                    <div className="st">{t.status}</div>
                  </div>
                </div>
              ))}
              {relatedAuths.map((a) => (
                <div className="flowchip flowchip--warn" key={a.id}>
                  Authorisation awaiting you
                </div>
              ))}
            </div>
          )}

          {hasMemories && (
            <div className="panel">
              <h3>What we know</h3>
              {memories.slice(0, 6).map((m) => (
                <div className="memory" key={m.id}>
                  {m.name}
                  <span className="src">
                    {(m.memory_type ?? "memory")} · {formatRelativeTime(m.updated_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * №29 (S2) — the record subject's crumb, as its own component so the page's hook
 * order is untouched. The lifecycle status is a real field on the record; when the
 * record has none, no chip is rendered rather than a neutral default.
 */
function RecordStageHead({ title, status }: { title: string; status?: string }) {
  useStageHead({ title, state: status ? { tone: "neutral", label: status } : null });
  return null;
}
